import { ImagePaths } from '../../lib/imageUrls.js';

export const microsoft365Security = {

    id: 8,
    slug: 'microsoft-365-security',
    ogImage: '/og/courses/microsoft-365-security.jpg',
    courseCode: 'M365-SEC-001',
    featured: true,
    title: 'Microsoft 365 Security Hardening',
    subtitle: 'Secure Microsoft 365 environments using ACSC guidelines',
    valueProposition: 'Protect your organisation\'s Microsoft 365 environment with enterprise-grade security controls aligned to ACSC Essential Eight and industry best practices.',
    duration: '16 hours',
    students: 'New',
    level: 'Intermediate',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_3,
    shortDescription: 'M365-SEC-001 — Microsoft 365 security hardening covering Conditional Access, Defender and DLP aligned to ACSC Essential Eight for M365 administrators.',
    price: {
      aud: 999,
      currency: 'AUD',
      earlyBird: 799
    },
    stripeConfig: {
    priceId: 'price_1SdKRPKu1nyc322U2rZ71CCD',
    productId: 'prod_TaVSE7GuRkYrPK'
  },
    
    snapshot: {
      whoItsFor: [
        'IT administrators managing Microsoft 365 environments',
        'Security officers implementing M365 security controls',
        'Compliance managers responsible for cloud security',
        'System administrators transitioning to cloud security'
      ],
      accreditation: 'Aligned to ACSC Essential Eight, ISO 27001, and Microsoft Security Best Practices'
    },
    
    logistics: {
      classSize: 'Maximum 12 participants for hands-on attention',
      requirements: [
        'Active Microsoft 365 tenant (trial acceptable)',
        'Global Administrator or Security Administrator role',
        'Laptop with modern browser (Edge or Chrome recommended)',
        'Reliable internet connection (minimum 10 Mbps)'
      ]
    },
    
    overview: 'Master Microsoft 365 security hardening with practical, hands-on training designed for Australian organisations. This comprehensive course covers identity protection, data security, threat protection, and compliance aligned to ACSC Essential Eight strategies. You\'ll learn to configure and manage Microsoft 365 security controls including Conditional Access, Data Loss Prevention, Microsoft Defender, and Azure AD security features through real-world scenarios and hands-on labs.',
    
    outcomes: [
      'Configure Conditional Access policies and multi-factor authentication aligned to Essential Eight requirements',
      'Implement Data Loss Prevention (DLP) policies to protect sensitive Australian organisational data',
      'Deploy and manage Microsoft Defender for Office 365 to prevent phishing and malware attacks',
      'Secure Azure Active Directory using identity protection and privileged access management',
      'Configure Microsoft Information Protection for data classification and encryption',
      'Monitor security threats using Microsoft 365 Defender and Security Score',
      'Implement compliance controls aligned to Australian Privacy Principles and ISO 27001',
      'Conduct security assessments and remediate vulnerabilities in Microsoft 365 environments'
    ],
    
    audience: [
      'IT administrators managing M365 environments',
      'Security officers and compliance managers',
      'System administrators responsible for cloud security',
      'IT professionals implementing Essential Eight',
      'Managed service providers supporting M365 clients'
    ],
    
    prerequisites: [
      'Basic understanding of Microsoft 365 administration',
      'Familiarity with Azure Active Directory concepts',
      'Understanding of information security fundamentals',
      'Experience with Microsoft 365 admin portal (helpful but not required)'
    ],
    
    modules: [
      {
        title: 'Microsoft 365 Security Foundations',
        duration: '3 hours',
        topics: [
          'Microsoft 365 security architecture and shared responsibility model',
          'Azure Active Directory security fundamentals',
          'Microsoft 365 Security Center overview and navigation',
          'Security baseline and Microsoft Secure Score',
          'Essential Eight alignment with Microsoft 365 controls'
        ]
      },
      {
        title: 'Identity and Access Management',
        duration: '4 hours',
        topics: [
          'Multi-factor authentication (MFA) deployment and enforcement',
          'Conditional Access policies for risk-based authentication',
          'Privileged Identity Management (PIM) and just-in-time access',
          'Identity Protection and risk detection',
          'Self-service password reset and authentication methods'
        ]
      },
      {
        title: 'Threat Protection',
        duration: '3 hours',
        topics: [
          'Microsoft Defender for Office 365 (Safe Links, Safe Attachments)',
          'Anti-phishing and anti-spam policies',
          'Zero-hour auto purge (ZAP) configuration',
          'Attack simulation training and security awareness',
          'Threat hunting with Microsoft 365 Defender'
        ]
      },
      {
        title: 'Information Protection and Compliance',
        duration: '3 hours',
        topics: [
          'Sensitivity labels and data classification strategies',
          'Data Loss Prevention (DLP) policies for Australian organisations',
          'Microsoft Information Protection and Azure Information Protection',
          'Retention policies and compliance management',
          'eDiscovery and audit logging for compliance requirements'
        ]
      },
      {
        title: 'Security Monitoring and Incident Response',
        duration: '3 hours',
        topics: [
          'Microsoft 365 Defender portal and unified security operations',
          'Alert investigation and automated response',
          'Security reports and analytics',
          'Incident response procedures for M365 security events',
          'Continuous monitoring and security posture improvement'
        ]
      }
    ],
    
    format: {
      type: 'Live online with hands-on labs',
      includes: [
        '16 hours of instructor-led training',
        'Hands-on lab exercises in live M365 environments',
        'Security configuration templates and checklists',
        'Essential Eight alignment documentation',
        'Policy templates for Australian organisations',
        'Certificate of completion',
        '6 months access to course materials'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Two-day intensive format (8 hours per day) with optional follow-up session'
    },
    
    faqs: [
      {
        question: 'Do I need a Microsoft 365 tenant to participate?',
        answer: 'Yes, you\'ll need access to a Microsoft 365 tenant with administrative privileges for hands-on labs. A trial tenant is acceptable, and we\'ll provide guidance on setting one up if needed. Your organisation\'s production environment can also be used with appropriate change management procedures.'
      },
      {
        question: 'What Microsoft 365 licences are required for the security features?',
        answer: 'Many security features require Microsoft 365 E3 or E5 licences. We cover both E3 and E5 capabilities and explain licensing requirements for each feature. The course focuses on practical implementation regardless of your current licensing tier.'
      },
      {
        question: 'How does this course align with ACSC Essential Eight?',
        answer: 'This course specifically addresses Essential Eight strategies including multi-factor authentication, application control, user application hardening, and restricting administrative privileges within Microsoft 365 environments. You\'ll learn to map Microsoft 365 security controls to Essential Eight maturity levels.'
      },
      {
        question: 'Is this course suitable for organisations new to Microsoft 365?',
        answer: 'Yes, while basic M365 familiarity is helpful, we cover security fundamentals from the ground up. The course is designed for organisations at any stage of their Microsoft 365 journey, from initial deployment to mature environments seeking hardening.'
      },
      {
        question: 'Will I receive configuration templates and documentation?',
        answer: 'Absolutely. You\'ll receive security baseline templates, configuration checklists, policy templates tailored for Australian organisations, and implementation guides that you can immediately apply in your environment.'
      },
      {
        question: 'What support is available after course completion?',
        answer: 'You\'ll have 6 months access to course materials. We also offer optional post-course consulting to assist with implementation in your specific environment.'
      },
      {
        question: 'Does this course prepare me for Microsoft security certifications?',
        answer: 'While not specifically a certification preparation course, the content aligns with Microsoft SC-300 (Identity and Access Administrator) and SC-200 (Security Operations Analyst) certifications and provides excellent foundational knowledge for those exams.'
      },
      {
        question: 'Can this course be delivered in-house for my organisation?',
        answer: 'Yes, we offer customised in-house delivery for organisations across Australia. We can tailor content to your specific M365 configuration, security requirements, and compliance obligations. Contact us to discuss your requirements and receive a quote.'
      }
    ]
};
